import React from 'react';
import Button from './Button';

export interface PaginationMeta {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
}

interface PaginationProps {
  meta: PaginationMeta;
  onChange: (page: number) => void;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  loading?: boolean;
  compact?: boolean;
}

const range = (start: number, end: number) => {
  const arr: number[] = [];
  for (let i = start; i <= end; i++) arr.push(i);
  return arr;
};

const Pagination: React.FC<PaginationProps> = ({
  meta,
  onChange,
  pageSizeOptions = [10, 20, 50],
  onPageSizeChange,
  loading,
  compact,
}) => {
  const { page, totalPages, total, limit } = meta;

  // Always show selector if total >= 5 even when only one page; otherwise hide completely
  const singlePage = totalPages <= 1;
  if (singlePage && total < 5) return null;

  // Determine pages to show (windowed)
  const windowSize = 5;
  let start = Math.max(1, page - Math.floor(windowSize / 2));
  let end = start + windowSize - 1;
  if (end > totalPages) {
    end = totalPages;
    start = Math.max(1, end - windowSize + 1);
  }
  const pages = range(start, end);

  return (
    <div
      className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 px-1 ${compact ? 'mt-4' : 'mt-6'}`}
    >
      <div className="text-sm text-neutral-600">
        Showing <span className="font-semibold text-neutral-900">{(page - 1) * limit + 1}</span>–
        <span className="font-semibold text-neutral-900">{Math.min(page * limit, total)}</span> of{' '}
        <span className="font-semibold text-neutral-900">{total}</span>
      </div>

      <div className="flex items-center gap-4">
        {onPageSizeChange && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-neutral-600">Per page</span>
            <select
              className="border border-neutral-300 rounded-lg text-sm px-3 pr-8 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all cursor-pointer hover:border-neutral-400"
              value={limit}
              onChange={e => onPageSizeChange(Number(e.target.value))}
              disabled={loading}
            >
              {pageSizeOptions.map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
        {!singlePage && (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 1 || loading}
              onClick={() => onChange(1)}
              className="min-w-[32px]"
            >
              «
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page === 1 || loading}
              onClick={() => onChange(page - 1)}
              className="min-w-[32px]"
            >
              ‹
            </Button>
            {pages.map(p => (
              <Button
                key={p}
                size="sm"
                variant={p === page ? 'primary' : 'outline'}
                onClick={() => onChange(p)}
                disabled={loading}
                className="min-w-[36px]"
              >
                {p}
              </Button>
            ))}
            <Button
              size="sm"
              variant="outline"
              disabled={page === totalPages || loading}
              onClick={() => onChange(page + 1)}
              className="min-w-[32px]"
            >
              ›
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page === totalPages || loading}
              onClick={() => onChange(totalPages)}
              className="min-w-[32px]"
            >
              »
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination;
